"use client";

import { memo } from "react";

import {
  TutorialCard as TutorialCardUI,
  type TutorialCardProgress,
} from "@vllnt/ui";
import Link from "next/link";
import { useTranslations } from "next-intl";

import type { TutorialMeta } from "@/types/tutorial";

function getTutorialProgress(contentId: string): null | TutorialCardProgress {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = localStorage.getItem("bntvllnt:tutorials:v1");
    if (!raw) return null;

    const store = JSON.parse(raw) as {
      tutorials: Record<
        string,
        {
          sections: Record<string, { completed: boolean }>;
        }
      >;
      version: number;
    };

    const entry = store.tutorials[contentId];
    if (!entry) return null;

    const completedSections = Object.values(entry.sections).filter(
      (s) => s.completed,
    );
    if (completedSections.length === 0) return null;

    return {
      completedCount: completedSections.length,
      totalSections: Object.keys(entry.sections).length,
    };
  } catch {
    return null;
  }
}

export type TutorialCardProps = {
  href: string;
  locale: string;
  tutorial: TutorialMeta;
};

function TutorialCardImpl({
  href,
  locale,
  tutorial,
}: TutorialCardProps): React.ReactNode {
  const t = useTranslations("tutorials");

  const labels = {
    completed: t("completed"),
    difficulty: {
      advanced: t("difficulty.advanced"),
      beginner: t("difficulty.beginner"),
      intermediate: t("difficulty.intermediate"),
    },
    sectionsCount: t("sections_count"),
  };

  return (
    <TutorialCardUI
      getProgress={getTutorialProgress}
      href={`/${locale}${href}`}
      labels={labels}
      linkComponent={Link}
      tutorial={{
        description: tutorial.description,
        difficulty: tutorial.difficulty,
        estimatedTime: tutorial.estimatedTime,
        id: tutorial.contentId,
        sectionCount: tutorial.sectionCount,
        tags: tutorial.tags,
        title: tutorial.title,
      }}
    />
  );
}

export const TutorialCard = memo(TutorialCardImpl);
TutorialCard.displayName = "TutorialCard";
